/**
 * Cut wizard cart helpers (non-Plus stores).
 * Fee line uses a hidden product variant; quantity encodes surcharge when variant unit price = fee unit cents.
 */
window.CutWizardCart = window.CutWizardCart || {
  config: {
    feeVariantId: 51804432236760,
    feeUnitCents: 1,
  },
  /** @param {unknown} centsPerPack @param {unknown} packQty @param {number} [feeUnitCents] */
  surchargeLineQuantity(centsPerPack, packQty, feeUnitCents = 1) {
    const cents = parseInt(String(centsPerPack ?? '').trim(), 10);
    const packs = parseInt(String(packQty ?? '').trim(), 10);
    const unit = parseInt(String(feeUnitCents ?? '1'), 10) || 1;
    if (!Number.isFinite(cents) || cents <= 0) return 0;
    if (!Number.isFinite(packs) || packs <= 0) return 0;
    return Math.ceil((cents * packs) / unit);
  },

  /** @param {FormData} formData */
  readSurchargeCentsPerPack(formData) {
    const raw =
      formData.get('properties[Cut selections - surcharges (cents)]') ??
      formData.get('properties[Cut selections — surcharges (cents)]');
    const cents = parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  },

  /** @param {FormData} formData */
  readPropertiesFromForm(formData) {
    /** @type {Record<string, string>} */
    const properties = {};
    formData.forEach((value, key) => {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) properties[match[1]] = String(value);
    });
    return properties;
  },

  /** @param {string} lineKey @param {string|number} parentVariantId @param {number} centsPerPack */
  buildSurchargeProperties(lineKey, parentVariantId, centsPerPack) {
    return {
      _cut_wizard_surcharge: '1',
      _cut_wizard_parent_variant: String(parentVariantId),
      _cut_wizard_line_key: lineKey,
      'Cut selections - surcharges (cents)': String(centsPerPack),
    };
  },

  /** @param {{properties?: Record<string, string>}} item */
  surchargeCentsPerPack(item) {
    const raw =
      item?.properties?.['Cut selections - surcharges (cents)'] ??
      item?.properties?.['Cut selections — surcharges (cents)'];
    const cents = parseInt(String(raw ?? '').trim(), 10);
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  },

  /** @param {{properties?: Record<string, string>}} item */
  isSurchargeLine(item) {
    return item?.properties?._cut_wizard_surcharge === '1';
  },

  /** @param {Array<{properties?: Record<string, string>, variant_id?: number, id?: number}>} items @param {{properties?: Record<string, string>}} surchargeLine */
  findLinkedPackIndex(items, surchargeLine) {
    if (!Array.isArray(items) || !this.isSurchargeLine(surchargeLine)) return -1;
    const lineKey = surchargeLine.properties?._cut_wizard_line_key;
    const parentVariantId = surchargeLine.properties?._cut_wizard_parent_variant;
    if (lineKey) {
      const byKey = items.findIndex(
        (it) =>
          !this.isSurchargeLine(it) &&
          String(it.properties?._cut_wizard_line_key || '') === String(lineKey)
      );
      if (byKey >= 0) return byKey;
    }
    if (!parentVariantId) return -1;
    return items.findIndex((it) => {
      if (this.isSurchargeLine(it)) return false;
      const variantId = String(it.variant_id ?? it.id ?? '');
      if (variantId !== String(parentVariantId)) return false;
      return this.surchargeCentsPerPack(it) > 0;
    });
  },

  /** @param {Array<{properties?: Record<string, string>}>} items @param {{properties?: Record<string, string>}} packLine */
  findLinkedSurchargeIndex(items, packLine) {
    if (!Array.isArray(items) || !packLine) return -1;
    const lineKey = packLine?.properties?._cut_wizard_line_key;
    const parentVariantId = packLine?.properties?._cut_wizard_parent_variant;
    if (lineKey) {
      const byKey = items.findIndex(
        (it) =>
          it?.properties?._cut_wizard_surcharge &&
          String(it.properties._cut_wizard_line_key || '') === String(lineKey)
      );
      if (byKey >= 0) return byKey;
    }
    if (!parentVariantId) return -1;
    return items.findIndex((it) => {
      if (!it?.properties?._cut_wizard_surcharge) return false;
      return String(it.properties._cut_wizard_parent_variant || '') === String(parentVariantId);
    });
  },

  createLineKey() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  },

  /**
   * Surcharge fee lines whose parent pack is no longer in the cart.
   * @param {Array<{properties?: Record<string, string>}>} items
   * @returns {number[]} 1-based cart line numbers, highest first
   */
  findOrphanedSurchargeLines(items) {
    if (!Array.isArray(items)) return [];
    /** @type {number[]} */
    const orphans = [];
    items.forEach((item, index) => {
      if (!this.isSurchargeLine(item)) return;
      if (this.findLinkedPackIndex(items, item) < 0) orphans.push(index + 1);
    });
    return orphans.sort((a, b) => b - a);
  },

  /**
   * Pack lines with a linked surcharge whose fee quantity is out of sync.
   * @param {Array<{properties?: Record<string, string>, quantity?: number}>} items
   * @param {number} [feeUnitCents]
   */
  findSurchargeQtyMismatches(items, feeUnitCents = 1) {
    if (!Array.isArray(items)) return [];
    /** @type {Array<{packLine: number, surchargeLine: number, desiredQty: number}>} */
    const mismatches = [];
    items.forEach((pack, packIndex) => {
      if (this.isSurchargeLine(pack)) return;
      const centsPerPack = this.surchargeCentsPerPack(pack);
      if (centsPerPack <= 0) return;
      const surchargeIndex = this.findLinkedSurchargeIndex(items, pack);
      if (surchargeIndex < 0) return;
      const packQty = parseInt(String(pack.quantity ?? '0'), 10) || 0;
      const desiredQty = this.surchargeLineQuantity(centsPerPack, packQty, feeUnitCents);
      const surcharge = items[surchargeIndex];
      const currentQty = parseInt(String(surcharge?.quantity ?? '0'), 10) || 0;
      if (currentQty !== desiredQty) {
        mismatches.push({
          packLine: packIndex + 1,
          surchargeLine: surchargeIndex + 1,
          desiredQty,
        });
      }
    });
    return mismatches;
  },
};
